// src/pages/Notifications.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  ShoppingBag as ShoppingBagIcon,
  HowToReg as HowToRegIcon,
  Cake as CakeIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Send as SendIcon,
  MailOutline as MailOutlineIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { Layout } from './Layout';

const ROLES = ['Élève', 'Élève adulte', 'Parent', 'Professeur'];

function getNotificationIcon(type) {
  switch (type) {
    case 'product_order':
      return <ShoppingBagIcon />;
    case 'registration':
      return <HowToRegIcon />;
    case 'birthday':
      return <CakeIcon />;
    default:
      return <InfoIcon />;
  }
}

function getNotificationLabel(type) {
  switch (type) {
    case 'product_order':
      return 'Commande';
    case 'registration':
      return 'Inscription';
    case 'birthday':
      return 'Anniversaire';
    default:
      return 'Info';
  }
}

function formatDate(value) {
  try {
    const date = value?.toDate?.() || (value ? new Date(value) : null);
    if (!date || Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('fr-BE', {
      weekday: 'short',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Composition d'une notification personnalisée ──────────────────────
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [audienceType, setAudienceType] = useState('all'); // all | role | course | users
  const [audienceRole, setAudienceRole] = useState(ROLES[0]);
  const [audienceCourseId, setAudienceCourseId] = useState('');
  const [audienceUsers, setAudienceUsers] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  const resetComposeForm = () => {
    setComposeTitle('');
    setComposeMessage('');
    setAudienceType('all');
    setAudienceRole(ROLES[0]);
    setAudienceCourseId('');
    setAudienceUsers([]);
    setSendError('');
    setSendSuccess('');
  };

  const openCompose = async () => {
    setComposeOpen(true);
    resetComposeForm();
    setOptionsLoading(true);
    try {
      const [coursesSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, 'dance_courses')),
        getDocs(collection(db, 'users'))
      ]);

      setCourseOptions(
        coursesSnap.docs
          .map((d) => ({ id: d.id, title: d.data().title || d.data().name || 'Cours' }))
          .sort((a, b) => a.title.localeCompare(b.title))
      );

      setUserOptions(
        usersSnap.docs
          .map((d) => {
            const data = d.data();
            const name = `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.pseudo || d.id;
            return { id: d.id, label: `${name}${data.role ? ` (${data.role})` : ''}` };
          })
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (e) {
      console.error('Erreur chargement options destinataires:', e);
      setSendError("Impossible de charger la liste des cours/utilisateurs.");
    } finally {
      setOptionsLoading(false);
    }
  };

  const closeCompose = () => {
    if (sending) return;
    setComposeOpen(false);
  };

  const resolveRecipients = async () => {
    if (audienceType === 'all') {
      const snap = await getDocs(collection(db, 'users'));
      return snap.docs.map((d) => d.id);
    }

    if (audienceType === 'role') {
      const snap = await getDocs(query(collection(db, 'users'), where('role', '==', audienceRole)));
      return snap.docs.map((d) => d.id);
    }

    if (audienceType === 'course') {
      if (!audienceCourseId) return [];
      const snap = await getDocs(
        query(collection(db, 'users'), where('selectedCourseIds', 'array-contains', audienceCourseId))
      );
      return snap.docs.map((d) => d.id);
    }

    // audienceType === 'users'
    return audienceUsers.map((u) => u.id);
  };

  const audienceLabel = () => {
    if (audienceType === 'all') return 'Tout le monde';
    if (audienceType === 'role') return `Rôle : ${audienceRole}`;
    if (audienceType === 'course') {
      const course = courseOptions.find((c) => c.id === audienceCourseId);
      return `Cours : ${course ? course.title : ''}`;
    }
    return `${audienceUsers.length} destinataire(s) sélectionné(s)`;
  };

  const handleSend = async () => {
    setSendError('');
    setSendSuccess('');

    if (!composeTitle.trim() || !composeMessage.trim()) {
      setSendError('Le titre et le message sont obligatoires.');
      return;
    }
    if (audienceType === 'course' && !audienceCourseId) {
      setSendError('Choisis un cours.');
      return;
    }
    if (audienceType === 'users' && audienceUsers.length === 0) {
      setSendError('Choisis au moins un destinataire.');
      return;
    }

    setSending(true);
    try {
      const recipients = await resolveRecipients();

      if (recipients.length === 0) {
        setSendError("Aucun destinataire trouvé pour ce ciblage.");
        setSending(false);
        return;
      }

      await addDoc(collection(db, 'personal_notifications'), {
        title: composeTitle.trim(),
        message: composeMessage.trim(),
        audienceType,
        audienceLabel: audienceLabel(),
        recipients,
        readBy: [],
        createdAt: serverTimestamp(),
        createdBy: getAuth().currentUser?.uid || null
      });

      setSendSuccess(`Notification envoyée à ${recipients.length} personne(s).`);
      setTimeout(() => setComposeOpen(false), 1200);
    } catch (e) {
      console.error('Erreur envoi notification:', e);
      setSendError("Erreur lors de l'envoi. Vérifie les règles Firestore.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setNotifications(snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data()
        })));
        setLoading(false);
        setError('');
      },
      (err) => {
        console.error('Erreur chargement notifications:', err);
        setError("Impossible de charger les notifications. Vérifie les règles Firestore.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => notification.read !== true).length,
    [notifications]
  );

  const filteredNotifications = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter((notification) => notification.read !== true);
    }

    if (filter === 'read') {
      return notifications.filter((notification) => notification.read === true);
    }

    return notifications;
  }, [filter, notifications]);

  const markAllAsRead = async () => {
    const unread = notifications.filter((notification) => notification.read !== true);
    if (!unread.length) return;

    const batch = writeBatch(db);
    unread.forEach((notification) => {
      batch.update(doc(db, 'notifications', notification.id), {
        read: true,
        readAt: serverTimestamp()
      });
    });

    await batch.commit();
  };

  const markOneAsRead = async (notification) => {
    if (notification.read === true) return;

    await updateDoc(doc(db, 'notifications', notification.id), {
      read: true,
      readAt: serverTimestamp()
    });
  };

  const deleteNotification = async (notificationId) => {
    await deleteDoc(doc(db, 'notifications', notificationId));
  };

  const openNotification = async (notification) => {
    await markOneAsRead(notification);

    if (notification.link) {
      navigate(notification.link);
      return;
    }

    if (notification.type === 'product_order') {
      navigate('/shop/orders');
    } else if (notification.type === 'registration') {
      navigate('/registrations');
    } else if (notification.type === 'birthday') {
      navigate('/students');
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Suis les nouvelles inscriptions, commandes, anniversaires et futurs modules.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<MailOutlineIcon />}
              onClick={openCompose}
            >
              Nouvelle notification
            </Button>
            <Button
              variant="contained"
              startIcon={<DoneAllIcon />}
              onClick={markAllAsRead}
              disabled={!unreadCount}
            >
              Tout marquer comme lu
            </Button>
          </Stack>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Toutes (${notifications.length})`}
              color={filter === 'all' ? 'primary' : 'default'}
              onClick={() => setFilter('all')}
            />
            <Chip
              label={`Non lues (${unreadCount})`}
              color={filter === 'unread' ? 'primary' : 'default'}
              onClick={() => setFilter('unread')}
            />
            <Chip
              label={`Lues (${notifications.length - unreadCount})`}
              color={filter === 'read' ? 'primary' : 'default'}
              onClick={() => setFilter('read')}
            />
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Chargement des notifications...
              </Typography>
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <NotificationsActiveIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
              <Typography variant="h6">Aucune notification</Typography>
              <Typography variant="body2" color="text.secondary">
                Les nouvelles commandes et inscriptions apparaîtront ici.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredNotifications.map((notification, index) => (
                <Box key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      px: 2,
                      py: 2,
                      bgcolor: notification.read === true ? 'white' : '#F8FAFC',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#F1F5F9' }
                    }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        {notification.read !== true && (
                          <Tooltip title="Marquer comme lu">
                            <IconButton
                              edge="end"
                              onClick={(event) => {
                                event.stopPropagation();
                                markOneAsRead(notification);
                              }}
                            >
                              <MarkEmailReadIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Supprimer">
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                    onClick={() => openNotification(notification)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: notification.read === true ? '#CBD5E1' : '#7C3AED' }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 8, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: notification.read === true ? 600 : 800 }}>
                            {notification.title || 'Notification'}
                          </Typography>

                          <Chip
                            size="small"
                            label={getNotificationLabel(notification.type)}
                            variant="outlined"
                          />

                          {notification.read !== true && (
                            <Chip size="small" label="Nouveau" color="error" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5, pr: 8 }}>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            {notification.message || ''}
                          </Typography>

                          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatDate(notification.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>

                  {index < filteredNotifications.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Paper>

        <Dialog open={composeOpen} onClose={closeCompose} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800 }}>Nouvelle notification personnalisée</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <TextField
                label="Titre"
                value={composeTitle}
                onChange={(e) => setComposeTitle(e.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                label="Message"
                value={composeMessage}
                onChange={(e) => setComposeMessage(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />

              <FormControl>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Destinataires
                </Typography>
                <RadioGroup
                  value={audienceType}
                  onChange={(e) => setAudienceType(e.target.value)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="Tout le monde" />
                  <FormControlLabel value="role" control={<Radio />} label="Par rôle" />
                  <FormControlLabel value="course" control={<Radio />} label="Par cours" />
                  <FormControlLabel value="users" control={<Radio />} label="Utilisateurs spécifiques" />
                </RadioGroup>
              </FormControl>

              {audienceType === 'role' && (
                <FormControl fullWidth>
                  <InputLabel>Rôle</InputLabel>
                  <Select
                    label="Rôle"
                    value={audienceRole}
                    onChange={(e) => setAudienceRole(e.target.value)}
                  >
                    {ROLES.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {audienceType === 'course' && (
                <FormControl fullWidth disabled={optionsLoading}>
                  <InputLabel>Cours</InputLabel>
                  <Select
                    label="Cours"
                    value={audienceCourseId}
                    onChange={(e) => setAudienceCourseId(e.target.value)}
                  >
                    {courseOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {audienceType === 'users' && (
                <Autocomplete
                  multiple
                  loading={optionsLoading}
                  options={userOptions}
                  value={audienceUsers}
                  onChange={(_, value) => setAudienceUsers(value)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(o) => o.label}
                  renderInput={(params) => <TextField {...params} label="Choisir les destinataires" />}
                />
              )}

              {sendError && <Alert severity="error">{sendError}</Alert>}
              {sendSuccess && <Alert severity="success">{sendSuccess}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeCompose} disabled={sending}>Annuler</Button>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending}
            >
              Envoyer
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
}

export default Notifications;